import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { useStrava } from '../contexts/StravaContext';
import SafeIcon from '../common/SafeIcon';

const { FiActivity } = FiIcons;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAuthCallback } = useStrava();
  const [status, setStatus] = useState('Processing your authentication...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const processAuth = async () => {
      try {
        // Get code from URL hash or search params
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const searchParams = new URLSearchParams(location.search);
        const code = hashParams.get('code') || searchParams.get('code');
        const error = hashParams.get('error') || searchParams.get('error');
        const state = hashParams.get('state') || searchParams.get('state');

        console.log('Auth callback received:', {
          code: code ? 'present' : 'missing',
          error,
          state,
          fullUrl: window.location.href,
          hashParams: location.hash,
          searchParams: location.search
        });

        if (error) {
          throw new Error(`Strava authorization error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code found in the URL');
        }

        setStatus('Connecting to Strava...');
        await handleAuthCallback(code);
        setStatus('Authentication successful! Redirecting...');
        setTimeout(() => navigate('/dashboard'), 1500);
      } catch (err) {
        console.error('Authentication error:', err);
        setError(err.message || 'Failed to authenticate with Strava');
        setStatus('Authentication failed');
      }
    };

    processAuth();
  }, [location, handleAuthCallback, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <motion.div
        className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {!error ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="mb-6"
            >
              <SafeIcon icon={FiActivity} className="w-16 h-16 text-orange-400 mx-auto" />
            </motion.div>
            <h2 className="text-2xl font-bold text-white mb-4">Connecting to Strava</h2>
            <p className="text-gray-300 mb-8">{status}</p>
            <div className="w-full bg-white/10 rounded-full h-2 mb-4">
              <motion.div
                className="bg-gradient-to-r from-orange-500 to-pink-500 h-2 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2.5 }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="text-red-400 bg-red-400/10 p-4 rounded-xl mb-6">
              <p className="font-medium">Authentication Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
            <p className="text-gray-300 mb-6">
              We couldn't connect to your Strava account. Please try again or use demo mode.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                Back to Home
              </button>
              <button
                onClick={() => { navigate('/dashboard'); }}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-lg transition-colors"
              >
                Try Demo Mode
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AuthCallback;